import { t } from 'utils/i18n';
import type {
    Scene, SceneItem, SceneMaterial, SceneMode, SceneOverride,
} from './scene-api';

// project: a material of the project; fallback: assembly picks one (see fallbacks.json);
// model: the furniture keeps the material of its .blend; library: a model of the shared library;
// none: no model, the objects are left out; other: a path the project or library does not list
export type RowStatus = 'project' | 'fallback' | 'model' | 'library' | 'none' | 'other' | 'empty' | 'unused';

export interface SceneRow {
    id: string;
    kind: SceneItem['kind'];
    // English, translated by the panel
    title: string;
    status: RowStatus;
    // what the shapes get, when it has a name
    name: string | null;
    preview: string | null;
    count: number;
    exceptions: number;
    // the fallback is not in its file: assembly leaves these shapes without a material
    missing: boolean;
    // the mode the converter uses the row in, when it is not the current one
    onlyIn: SceneMode | null;
    // the scene value and its short name ("!BR_Parquet"); shapes holding another one
    value: string | null;
    sceneName: string;
    overrides: OverrideLine[];
}

export interface OverrideLine extends SceneOverride {
    name: string;
    preview: string | null;
}

export interface FolderLine {
    path: string;
    present: boolean;
    // English: what assembly takes from the folder, or what it does without it
    note: string;
    // VR export refuses to start without it
    blocksExport: boolean;
}

const ITEM_TITLES: Record<string, string> = {
    wall: 'Walls', surface: 'Countertops', spot: 'Spots', curtain: 'Curtains', tule: 'Tulle',
};

// the step whose shapes the floor rows come from
export const ROOMS_STEP = 'Detect rooms';

// room_* labels of labels_rooms.json
const ROOM_TITLES: Record<string, string> = {
    room_livingroom: 'Living room',
    room_bedroom: 'Bedroom',
    room_masterbedroom: 'Master bedroom',
    room_guestroom: 'Guest room',
    room_childrensroom: "Children's room",
    room_bathroom: 'Bathroom',
    room_kitchen: 'Kitchen',
    room_dining: 'Dining room',
    room_pantry: 'Pantry',
    room_study: 'Study room',
    room_laundry: 'Laundry',
    room_storage: 'Storage room',
    room_hometheater: 'Home theater',
    room_gym: 'Gym',
    room_hallway: 'Hallway',
    room_garage: 'Garage',
    room_mechanicalroom: 'Mechanical room',
    room_closet: 'Closet',
    room_terrace: 'Terrace',
    room_balcony: 'Balcony',
    room_livngdinning: 'Living-dining room',
    room_maids: "Maid's room",
    room_majlis: 'Majlis',
    room_serviceroom: 'Service room',
    room_familyroom: 'Family room',
};

const FLOOR = 'floor:';

export function isFloor(id: string): boolean {
    return id.startsWith(FLOOR);
}

// the scene item a label's attribute belongs to (views._scene_item_of)
const MODEL_ITEMS: Record<string, string> = {
    'spot/spot_model': 'spot', 'window/curtain_model': 'curtain', 'window/tule_model': 'tule',
};

export function sceneItemOf(labelName: string, attrName: string): string | null {
    if (attrName !== 'material') return MODEL_ITEMS[`${labelName}/${attrName}`] ?? null;
    if (labelName.startsWith('room_')) return `${FLOOR}${labelName}`;
    if (labelName === 'wall') return 'wall';
    return labelName === 'kitchenset' || labelName === 'sink' ? 'surface' : null;
}

export function itemTitle(id: string): string {
    if (isFloor(id)) {
        const label = id.slice(FLOOR.length);
        return ROOM_TITLES[label] ?? label;
    }
    return ITEM_TITLES[id] ?? id;
}

// "!BTH_Tile" of "/p/General_Materials/FP/Finishes.blend#material:!BTH_Tile", "Spot" of ".../Spot/Spot.blend"
export function materialName(path: string): string {
    const [file, name] = path.split('#material:');
    return name ?? (file.split(/[\\/]/).pop() || path).replace(/\.blend$/i, '');
}

export function itemStatus(item: SceneItem, mode: SceneMode): RowStatus {
    if (!item.modes.includes(mode)) return 'unused';
    if (item.count === 0) return 'empty';
    const listed = item.candidates.some((candidate) => candidate.path === item.value);
    if (item.kind === 'model') {
        if (!item.value) return 'none';
        return listed ? 'library' : 'other';
    }
    if (!item.value) return item.id === 'surface' ? 'model' : 'fallback';
    return listed ? 'project' : 'other';
}

// an empty value of the item as a select names it: the fallback with its file, no model, the furniture's material
function emptyName(item: SceneItem): string {
    if (item.kind === 'model') return t('No model');
    if (item.id === 'surface') return t('The material of the furniture model');
    if (!item.fallback) return t('The converter fallback');
    const { name, file } = item.fallback;
    return file ? `${name} · ${file}` : name;
}

// a value of the item in short: "!BR_Parquet", the fallback's name, "No model"
function shortName(item: SceneItem, value: string): string {
    return value ? materialName(value) : item.fallback?.name ?? emptyName(item);
}

function overrideLine(item: SceneItem, override: SceneOverride): OverrideLine {
    const candidate = item.candidates.find((material) => material.path === override.value);
    let name = item.fallback ? t('Fallback: {name}', { name: emptyName(item) }) : emptyName(item);
    if (candidate) name = item.kind === 'model' ? materialName(candidate.name) : candidate.name;
    else if (override.value) name = materialName(override.value);
    const fallback = override.value ? null : item.fallback?.preview ?? null;
    return { ...override, name, preview: candidate?.image_path ?? fallback };
}

export function sceneRows(scene: Scene): SceneRow[] {
    return scene.items.map((item) => {
        const status = itemStatus(item, scene.mode);
        const candidate = item.candidates.find((material) => material.path === item.value);
        const fallback = status === 'fallback' ? item.fallback : null;
        let name: string | null = fallback?.name ?? null;
        if (candidate) name = item.kind === 'model' ? materialName(candidate.name) : candidate.name;
        else if (status === 'other' && item.value) name = materialName(item.value);
        return {
            id: item.id,
            kind: item.kind,
            title: itemTitle(item.id),
            status,
            name,
            preview: candidate?.image_path ?? fallback?.preview ?? null,
            count: item.count,
            exceptions: item.exceptions,
            missing: !!fallback?.missing,
            onlyIn: status === 'unused' ? item.modes[0] : null,
            value: item.value,
            sceneName: item.value === null ? '' : shortName(item, item.value),
            overrides: item.overrides.map((override) => overrideLine(item, override)),
        };
    });
}

const STATUS_TAGS: Partial<Record<RowStatus, { color: string, text: string }>> = {
    project: { color: 'green', text: 'Project' },
    library: { color: 'green', text: 'Library' },
    fallback: { color: 'gold', text: 'Fallback' },
    none: { color: 'gold', text: 'No model' },
    model: { color: 'default', text: 'As in the model' },
    other: { color: 'blue', text: 'Other' },
};

// why a row has nothing to show, in the user's terms
const UNUSED: Record<string, string> = {
    wall: 'VR walls take no material: they have no material input',
    curtain: 'FP windows are built without curtains',
    tule: 'FP windows are built without tulle',
};
const EMPTY: Record<string, string> = {
    wall: 'No walls on the drawings',
    surface: 'No kitchens or sinks on the drawings',
    spot: 'No spots on the drawings',
    curtain: 'No windows on the drawings',
    tule: 'No windows on the drawings',
};
const NO_MODEL: Record<string, string> = {
    spot: 'No model: the spots are not placed',
    curtain: 'No model: the windows get no curtains',
    tule: 'No model: the windows get no tulle',
};

// the status tag of a row, translated; null when the name line says it all
export function rowTag(row: SceneRow): { color: string, text: string } | null {
    if (row.onlyIn) return { color: 'default', text: t('{mode} only', { mode: row.onlyIn }) };
    const tag = STATUS_TAGS[row.status];
    return tag ? { ...tag, text: t(tag.text) } : null;
}

// what an empty model value means for the objects of an item
export function noModel(itemId: string): string {
    return t(NO_MODEL[itemId] ?? 'No model');
}

// the name line explains why there is nothing to name: shown whole, never cut
export function explains(row: SceneRow): boolean {
    return ['empty', 'none', 'model'].includes(row.status) || (row.status === 'unused' && !row.name);
}

// why a row with a value is not used in this mode, shown next to its count
export function unusedNote(row: SceneRow, mode: SceneMode): string | null {
    return row.status === 'unused' && row.name ? t(UNUSED[row.id] ?? 'Not used in {mode}', { mode }) : null;
}

// what the shapes of a row get, in words
export function rowName(row: SceneRow, mode: SceneMode): string {
    switch (row.status) {
        // a model kept for the other mode is named; the count line says why it is not used here
        case 'unused': return row.name ?? t(UNUSED[row.id] ?? 'Not used in {mode}', { mode });
        case 'empty': return t(EMPTY[row.id] ?? 'Not in the drawings');
        case 'none': return noModel(row.id);
        case 'model': return t('The material of the furniture model');
        case 'fallback':
            if (!row.name) return t('The converter fallback');
            return row.missing ? t('{name}: not in its file, no material', { name: row.name }) : row.name;
        default: return row.name ?? '';
    }
}

// what a shape's select knows of the scene: another value is the object's own
export interface SceneAttribute {
    value: string;
    // the scene value in short, for "As in the scene: …"
    shortName: string;
    // an empty value as the select names it: the fallback with its file, no model, the furniture's material
    emptyName: string;
    emptyPreview: string | null;
    // for a shape that has no options of its own, e.g. one just drawn
    options: SceneMaterial[];
}

export function sceneAttribute(scene: Scene | null, labelName: string, attrName: string): SceneAttribute | null {
    const id = sceneItemOf(labelName, attrName);
    const item = scene?.items.find((candidate) => candidate.id === id);
    if (!item || item.value === null) return null;
    return {
        value: item.value,
        shortName: shortName(item, item.value),
        emptyName: emptyName(item),
        emptyPreview: item.fallback?.preview ?? null,
        options: item.candidates,
    };
}

// the attributes a shape drawn now gets: the scene value of each its label has
export function sceneDefaults(
    scene: Scene | null, label: { name: string, attributes: { id: number, name: string }[] },
): Record<number, string> {
    const defaults: Record<number, string> = {};
    for (const attribute of label.attributes) {
        const known = sceneAttribute(scene, label.name, attribute.name);
        if (known) defaults[attribute.id] = known.value;
    }
    return defaults;
}

// the header button's badge: rows of the current mode on a fallback or without a model
export function attentionCount(scene: Scene): number {
    return sceneRows(scene).filter((row) => row.status === 'fallback' || row.status === 'none').length;
}

// what assembly takes in this mode when the project has nothing set
export interface FallbackLine {
    title: string;
    name: string;
    preview: string | null;
    missing: boolean;
}

export function fallbackLines(scene: Scene): FallbackLine[] {
    return scene.items
        .filter((item) => item.fallback && item.modes.includes(scene.mode))
        .map((item) => ({
            title: itemTitle(item.id),
            name: item.fallback!.name,
            preview: item.fallback!.preview,
            missing: item.fallback!.missing,
        }));
}

// what assembly takes from each folder (asset_sets.resolve_asset, the room index, the materials index)
const FOLDER_ROLES = [
    {
        key: 'box', name: 'Box', gives: 'doors, windows, stairs', without: 'doors, windows and stairs from the shared library',
    },
    {
        key: 'rooms', name: 'Rooms', gives: 'furniture', without: 'furniture from the shared libraries only',
    },
    {
        key: 'materials', name: 'General_Materials', gives: 'materials', without: 'materials: the fallbacks',
    },
] as const;

export function folderLines(scene: Scene): FolderLine[] | null {
    if (!scene.folder || !scene.folders) return null;
    const flags = scene.folders[scene.mode];
    return FOLDER_ROLES.map((role) => ({
        path: `${role.name}/${scene.mode}`,
        present: flags[role.key],
        note: flags[role.key] ? role.gives : role.without,
        // views._check_vr_project_folder
        blocksExport: scene.mode === 'VR' && !flags[role.key],
    }));
}

export interface PickerGroup {
    // English, translated by the picker
    title: string;
    materials: SceneMaterial[];
}

// the candidates of an item: the library's models; or the project's materials named for this
// room first, then the item's category, then the ones whose category the indexer could not tell
export function pickerGroups(item: SceneItem): PickerGroup[] {
    if (item.kind === 'model') return [{ title: 'Library', materials: item.candidates }];
    const room = isFloor(item.id) ? item.id.slice(FLOOR.length) : null;
    const forRoom = item.candidates.filter((material) => room && material.room_type === room);
    const known = item.candidates.filter((material) => material.label_name && !forRoom.includes(material));
    const unknown = item.candidates.filter((material) => !material.label_name);
    return [
        { title: 'For this room', materials: forRoom },
        { title: 'Project materials', materials: known },
        { title: 'Without a category', materials: unknown },
    ].filter((group) => group.materials.length > 0);
}

export interface FolderRow {
    name: string;
    FP: boolean;
    VR: boolean;
}

// the three folders assembly reads, in both modes
export function folderTable(scene: Scene): FolderRow[] | null {
    if (!scene.folders) return null;
    const { FP, VR } = scene.folders;
    return [
        { name: 'Box', FP: FP.box, VR: VR.box },
        { name: 'Rooms', FP: FP.rooms, VR: VR.rooms },
        { name: 'General_Materials', FP: FP.materials, VR: VR.materials },
    ];
}
